import { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import './Alerts.css';
import DeviceView from '../../devices/components/DeviceView';
import AlertFilters from './AlertFilters';
import MoreFiltersButton from '../../../shared/components/common/MoreFiltersButton';
import AlertTable from './AlertTable';
import AlertHelpModal from './AlertHelpModal';
import Pagination from '../../../shared/components/common/Pagination';
import LocationFilterMultiSelect from '../../../shared/components/common/LocationFilterMultiSelect';
import DisabledLocationAccessBlockedBanner from '../../../shared/components/common/DisabledLocationAccessBlockedBanner';
import {
  findLocationById,
  formatLocationFilterOptionLabel,
  getDisabledLocationsFromSelection,
} from '../../../shared/utils/locationDisplay';
import {
  enrichAlert,
  getSubjectPrimarySortKey,
  groupAlerts,
} from '../utils/alertUtils';
import { API_ENDPOINTS } from '../../../config/apiConfig';
import {
  getListPageSize,
  getAlertsViewMode,
  saveUiPreferencePatch,
} from '../../../shared/utils/uiPreferences';

const SUBJECT_TYPE_OPTIONS = [
  { value: 'device', label: 'Device' },
  { value: 'complaint', label: 'Complaint' },
  { value: 'location', label: 'Location' },
  { value: 'user', label: 'User' },
  { value: 'organization', label: 'Organization' },
];

/**
 * Alerts Component - Main alerts page with Phase 1 UX redesign
 * Features: Smart grouping, advanced filtering, alert lifecycle management
 */
function Alerts({
  token,
  onDeviceEdit,
  user,
  onRefresh,
  onNavigateToApprovals,
  onNavigateToLocationTransfers,
  onNavigateToDepartmentTransfers,
  onNavigateToComplaintDetail,
  viewingDeviceId,
  setViewingDeviceId,
  showDeviceModal,
  setShowDeviceModal
}) {
  // =========================================================================
  // SECTION A: Core Alert Data
  // =========================================================================
  const [alerts, setAlerts] = useState([]);

  // Summary card counts - Updated based on current filters applied
  const [counts, setCounts] = useState({ critical: 0, warning: 0, info: 0, total: 0 });

  // =========================================================================
  // SECTION B: Filter Options (from GET /api/alerts/filters)
  // =========================================================================
  const [filterOptions, setFilterOptions] = useState({
    locations: [],
    severities: [],
    alert_types: [],
    statuses: [],
    date_range: { earliest: null, latest: null }
  });
  const [locations, setLocations] = useState([]);

  // =========================================================================
  // SECTION C: Advanced Filter State (for POST /api/alerts/filter)
  // =========================================================================
  const [filters, setFilters] = useState({
    location_ids: [],        // Multi-location IDs
    severity: [],            // [critical, warning, info] - from Priority dropdown
    categories: [],          // Category filter (multiselect) - converted to alert_types server-side
    device_id: null,         // Single device ID or null
    subject_types: [],       // device | complaint | location | ...
    department_ids: [],
    search: '',              // Search term
    start_date: null,        // ISO date or null (default: 30 days ago)
    end_date: null           // ISO date or null (default: today)
  });

  // =========================================================================
  // SECTION D: Pagination State (for POST /api/alerts/filter)
  // =========================================================================
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: getListPageSize(),
    total_count: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false
  });

  // =========================================================================
  // SECTION E: Sorting State
  // =========================================================================
  const [sorting, setSorting] = useState({
    sort_by: 'last_detected_at',
    sort_direction: 'desc'
  });

  // =========================================================================
  // SECTION F: Legacy UX Controls (Phase 1 - kept for backward compat)
  // =========================================================================
  const [groupBy, setGroupBy] = useState('timeline');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterCategories, setFilterCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState(getAlertsViewMode); // 'active', 'acknowledged', 'snoozed', 'archived'

  // Device modal state - now controlled by parent App.js (passed as props)

  // Loading states (full-page only on first load; table overlay on refetch/sort)
  const [loading, setLoading] = useState(true);
  const [isRefreshingAlerts, setIsRefreshingAlerts] = useState(false);
  const [error, setError] = useState('');

  // Help modal state
  const [showHelpModal, setShowHelpModal] = useState(false);

  // Advanced filters drawer state
  const [showAdvancedPanel, setShowAdvancedPanel] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    location: true,
    subjectType: true,
    department: false,
    device: false,
    dateRange: true,
  });
  const [drawerFilters, setDrawerFilters] = useState({
    location_ids: [],
    subject_types: [],
    department_ids: [],
    device_search: '',
    start_date: null,
    end_date: null,
  });

  // Debounce timer for search - prevents API call on every keystroke
  const [searchDebounceTimer, setSearchDebounceTimer] = useState(null);

  // User permissions
  const canEditDevices = user && ['superadmin', 'admin', 'coordinator', 'manager'].includes(user.role);

  const isDepartmentScopedRole = user && ['coordinator', 'custodian', 'technician'].includes(user.role);
  const isDepartmentFilterDisabled = isDepartmentScopedRole && user.departments && user.departments.length === 1;

  const getDepartmentFilterOptions = () => {
    if (isDepartmentScopedRole && user.departments?.length) {
      return user.departments;
    }
    return filterOptions.departments || [];
  };

  const departmentFilterOptions = getDepartmentFilterOptions();

  const locationFilterList = user?.role === 'superadmin'
    ? locations
    : (filterOptions.locations || []).map((loc) => ({
        id: loc.id,
        name: loc.name,
        is_active: true,
      }));

  const blockedDisabledLocations = useMemo(
    () => (user?.role === 'superadmin'
      ? getDisabledLocationsFromSelection(locations, filters.location_ids)
      : []),
    [user?.role, locations, filters.location_ids],
  );
  const isDisabledLocationFilterBlocked = blockedDisabledLocations.length > 0;

  // Helper function to check if advanced filters are applied
  const appliedFilterCount = [
    filters.location_ids.length,
    filters.subject_types.length,
    filters.department_ids.length,
    filters.device_id,
    filters.start_date || filters.end_date,
  ].filter(Boolean).length;

  const hasAdvancedFilters = () => appliedFilterCount > 0;

  // =========================================================================
  // EFFECT 0: Fetch filter options on mount
  // Purpose: Load available filter values (locations, severities, etc.)
  // =========================================================================
  useEffect(() => {
    if (token) {
      fetchFilterOptions();
    }
  }, [token]);

  useEffect(() => {
    if (token && user?.role === 'superadmin') {
      fetchLocations();
    }
  }, [token, user?.role]);

  // Add a small 'isReady' state to coordinate the mount
  const [isReady, setIsReady] = useState(false);

  // =========================================================================
  // EFFECT 1: THE ONLY API CALLER
  // =========================================================================
  useEffect(() => {
    // Only fetch if token is present AND we've checked for window options
    if (!token || !isReady) return;

    const controller = new AbortController();
    
    fetchAlerts(null, { signal: controller.signal });

    return () => controller.abort();
  }, [
    isReady, // New dependency
    pagination.page, 
    pagination.per_page, 
    sorting.sort_by, 
    sorting.sort_direction,
    filters.location_ids, filters.severity, filters.categories,
    filters.alert_type, filters.device_id, filters.subject_types, filters.department_ids,
    filters.search, filters.start_date, filters.end_date,
    token,
    isDisabledLocationFilterBlocked,
  ]);

  // =========================================================================
  // EFFECT 3: WINDOW/NAVIGATION (Run this FIRST)
  // =========================================================================
  useEffect(() => {
    if (window.alertFilterOptions) {
      const { device_identifier, subject_types } = window.alertFilterOptions;

      if (device_identifier) {
        setSearchQuery(device_identifier);
      }

      setFilters((prev) => ({
        ...prev,
        ...(device_identifier ? { search: device_identifier } : {}),
        ...(subject_types?.length ? { subject_types } : {}),
      }));

      if (subject_types?.length) {
        setDrawerFilters((prev) => ({ ...prev, subject_types }));
      }

      delete window.alertFilterOptions;
    }

    // Signal that we are ready to start fetching
    setIsReady(true);
  }, []);

  // Cleanup debounce timer on component unmount
  useEffect(() => {
    return () => {
      if (searchDebounceTimer) {
        clearTimeout(searchDebounceTimer);
      }
    };
  }, [searchDebounceTimer]);

  /**
   * Refresh alerts - Regenerate alerts from backend
   * This manually triggers alert recalculation for all accessible locations
   * Called when user clicks the refresh button (🔄)
   */
  const handleRefreshAlerts = async () => {
    try {
      setIsRefreshingAlerts(true);
      setError('');

      // Call POST /api/alerts/refresh to regenerate alerts
      const response = await axios.post(API_ENDPOINTS.ALERTS.REFRESH, {}, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      // Show success message
      const locationsCount = response.data.locations_refreshed || 1;
      const totalAlerts = response.data.total_alerts || 0;
      const newAlerts = response.data.new_alerts || 0;

      if (newAlerts > 0) {
        setError(`✅ Alerts refreshed! Found ${newAlerts} new alert${newAlerts !== 1 ? 's' : ''} across ${locationsCount} location${locationsCount !== 1 ? 's' : ''}.`);
      } else {
        setError(`✅ Alerts refreshed. No new alerts found (${totalAlerts} total).`);
      }

      // Refresh the alert data after regeneration
      await fetchAlerts();
    } catch (err) {
      console.error('Error refreshing alerts:', err);
      setError(err.response?.data?.error || 'Failed to refresh alerts. Please try again.');
    } finally {
      setIsRefreshingAlerts(false);
    }
  };

  /**
   * EFFECT 0 Function: Fetch available filter options for dropdowns
   * Calls GET /api/alerts/filters
   */
  const fetchFilterOptions = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.ALERTS.FILTERS, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      setFilterOptions(response.data);

      // Set initial counts from filter options (no filters applied yet)
      const criticalCount = response.data.severities?.find(s => s.value === 'critical')?.count || 0;
      const warningCount = response.data.severities?.find(s => s.value === 'warning')?.count || 0;
      const infoCount = response.data.severities?.find(s => s.value === 'info')?.count || 0;
      const totalCount = criticalCount + warningCount + infoCount;

      setCounts({
        critical: criticalCount,
        warning: warningCount,
        info: infoCount,
        total: totalCount
      });
    } catch (err) {
      console.error('Error fetching filter options:', err);
      setError(err.response?.data?.error || 'Failed to load filter options');
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.LOCATIONS.LIST, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLocations(response.data || []);
    } catch (err) {
      console.error('Error fetching locations:', err);
      setLocations([]);
    }
  };

  /**
   * EFFECTS 1 & 2 Function: Fetch alerts with advanced filtering
   * Calls POST /api/alerts/filter with pagination, filters, and sorting
   *
   * @param {Object} paginationParams - Optional pagination override (page, per_page)
   */
  const fetchAlerts = async (paginationParams = null, options = {}) => {
    const isInitialLoad = alerts.length === 0 && loading;
    if (isInitialLoad) {
      setLoading(true);
    } else {
      setIsRefreshingAlerts(true);
    }
    setError('');

    if (isDisabledLocationFilterBlocked) {
      setAlerts([]);
      setCounts({ critical: 0, warning: 0, info: 0, total: 0 });
      setPagination((prev) => ({
        ...prev,
        total_count: 0,
        total_pages: 0,
        has_next: false,
        has_prev: false,
      }));
      setLoading(false);
      setIsRefreshingAlerts(false);
      return;
    }

    try {
      // Use provided pagination params or current pagination state
      const params = paginationParams || pagination;

      // Build request payload for POST endpoint
      const payload = {
        pagination: {
          page: params.page,
          per_page: params.per_page
        },
        filters: {
          location_ids: filters.location_ids,
          severity: filters.severity,           // From Priority dropdown
          categories: filters.categories,       // Category multiselect (server-side conversion to alert_types)
          device_id: filters.device_id,
          subject_types: filters.subject_types,
          department_ids: filters.department_ids,
          search: filters.search,
          start_date: filters.start_date,
          end_date: filters.end_date,
        },
        sorting: sorting
      };

      // Call POST /api/alerts/filter endpoint
      const response = await axios.post(API_ENDPOINTS.ALERTS.FILTER, payload, {
        headers: { 'Authorization': `Bearer ${token}` },
        signal: options.signal,
      });

      // Enrich alerts with client-side data
      const enrichedAlerts = response.data.alerts.map((alert) => enrichAlert(alert));

      setAlerts(enrichedAlerts);

      // Update counts based on current filters from response
      // Summary cards show severity breakdown of CURRENT filtered results
      if (response.data.counts_by_severity) {
        const criticalCount = response.data.counts_by_severity.critical || 0;
        const warningCount = response.data.counts_by_severity.warning || 0;
        const infoCount = response.data.counts_by_severity.info || 0;
        const totalCount = criticalCount + warningCount + infoCount;

        setCounts({
          critical: criticalCount,
          warning: warningCount,
          info: infoCount,
          total: totalCount
        });
      }

      // Update pagination state from response
      setPagination({
        page: response.data.page,
        per_page: response.data.per_page,
        total_count: response.data.total_count,
        total_pages: response.data.total_pages,
        has_next: response.data.has_next,
        has_prev: response.data.has_previous
      });

      // Notify parent component to refresh the navigation badge count
      if (onRefresh) {
        onRefresh();
      }
    } catch (err) {
      console.error('Error fetching alerts:', err);
      setError(err.response?.data?.error || 'Failed to fetch alerts');
    } finally {
      setLoading(false);
      setIsRefreshingAlerts(false);
    }
  };

  /**
   * Toggle help modal - info is now static (no API call needed)
   */
  const toggleHelpModal = () => {
    setShowHelpModal(!showHelpModal);
  };

  /**
   * Update alert status (acknowledge, snooze, archive) via backend API
   * Phase 2 Production: Status is persisted to database via PATCH endpoints
   */
  const updateAlertStatus = async (alertId, statusUpdate) => {
    try {
      const status = statusUpdate.status;

      if (status === 'acknowledged') {
        // Call acknowledge endpoint
        await axios.patch(API_ENDPOINTS.ALERTS.ACKNOWLEDGE(alertId), {}, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
      } else if (status === 'snoozed') {
        // Call snooze endpoint
        await axios.patch(API_ENDPOINTS.ALERTS.SNOOZE(alertId), {}, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
      } else if (status === 'archived') {
        await axios.patch(API_ENDPOINTS.ALERTS.ARCHIVE(alertId), {}, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
      } else if (status === 'active' && statusUpdate.snoozed_until === null) {
        await axios.patch(API_ENDPOINTS.ALERTS.UNSNOOZE(alertId), {}, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
      }

      // Update local state after successful backend update
      setAlerts((prevAlerts) => {
        return prevAlerts.map((a) =>
          a.id === alertId ? { ...a, ...statusUpdate } : a
        );
      });

      // Refresh parent component's alert count badge after status update
      if (onRefresh) {
        onRefresh();
      }
    } catch (err) {
      console.error('Error updating alert status:', err);
      setError(err.response?.data?.error || 'Failed to update alert');
    }
  };

  /**
   * Handle severity filter change - updates server-side severity filter
   */
  const handleSeverityChange = (severityValue) => {
    if (severityValue === 'all') {
      setFilters(prev => ({ ...prev, severity: [] }));
    } else {
      setFilters(prev => ({ ...prev, severity: [severityValue] }));
    }
  };

  /**
   * Handle category filter change - updates server-side category multiselect
   */
  const handleCategoryChange = (categories) => {
    setFilters((prev) => ({ ...prev, categories }));
  };

  /**
   * Handle search input change - updates server-side search filter with debounce
   * Waits 700ms after user stops typing before making API call
   * This prevents unnecessary API calls on every keystroke
   */
  const handleSearchChange = (searchValue) => {
    // Apply immediately when clearing search
    if (!searchValue) {
      if (searchDebounceTimer) clearTimeout(searchDebounceTimer);
      setFilters(prev => ({ ...prev, search: '' }));
      setSearchDebounceTimer(null);
      return;
    }

    // Clear previous timer
    if (searchDebounceTimer) {
      clearTimeout(searchDebounceTimer);
    }

    // Set new timer - wait 700ms after user stops typing
    const timer = setTimeout(() => {
      setFilters(prev => ({ ...prev, search: searchValue }));
      setSearchDebounceTimer(null);
    }, 700);

    setSearchDebounceTimer(timer);
  };

  /**
   * Handle column sort change - toggle direction if same column, or set new column with asc
   */
  const handleSortChange = (column) => {
    setSorting(prev => {
      // If same column clicked, toggle direction; otherwise set new column with asc
      if (prev.sort_by === column) {
        return {
          ...prev,
          sort_direction: prev.sort_direction === 'asc' ? 'desc' : 'asc'
        };
      } else {
        return {
          sort_by: column,
          sort_direction: 'asc'
        };
      }
    });
  };

  /**
   * Clear all advanced filters (location, date range)
   */
  const handleClearAllFilters = () => {
    setFilters(prev => ({
      ...prev,
      location_ids: [],
      subject_types: [],
      department_ids: [],
      device_id: null,
      start_date: null,
      end_date: null,
    }));
    setPagination((prev) => ({ ...prev, page: 1 }));
    // Close the drawer if it was open
    setShowAdvancedPanel(false);
  };

  const updateAdvancedFilters = (partial) => {
    setFilters((prev) => ({ ...prev, ...partial }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const renderAdvancedFilterPills = () => {
    const pills = [];

    const pushPill = (key, label, onRemove) => {
      if (pills.length > 0) {
        pills.push(
          <span key={`and-${key}`} className="filter-and-connector"> AND </span>,
        );
      }
      pills.push(
        <div key={key} className="filter-pill">
          {label}
          <span className="filter-pill-remove" onClick={onRemove}>✕</span>
        </div>,
      );
    };

    if (filters.location_ids.length > 0) {
      const locationNames = filters.location_ids
        .map((id) => {
          const loc = findLocationById(locationFilterList, id)
            || filterOptions.locations?.find((l) => String(l.id) === String(id));
          return formatLocationFilterOptionLabel(loc);
        })
        .join(', ');
      pushPill('locations', `Location: ${locationNames}`, () => updateAdvancedFilters({ location_ids: [] }));
    }

    if (filters.subject_types.length > 0) {
      const subjectLabels = filters.subject_types
        .map((value) => SUBJECT_TYPE_OPTIONS.find((o) => o.value === value)?.label || value)
        .join(', ');
      pushPill('subject-types', `Subject type: ${subjectLabels}`, () => updateAdvancedFilters({ subject_types: [] }));
    }

    if (filters.department_ids.length > 0) {
      const departmentNames = filters.department_ids
        .map((id) => departmentFilterOptions.find((d) => d.id === id)?.name || id)
        .join(', ');
      pushPill('departments', `Department: ${departmentNames}`, () => updateAdvancedFilters({ department_ids: [] }));
    }

    if (filters.device_id) {
      pushPill('device', `Device ID: ${filters.device_id}`, () => updateAdvancedFilters({ device_id: null }));
    }

    if (filters.start_date || filters.end_date) {
      let dateLabel = 'Date: ';
      if (filters.start_date && filters.end_date) {
        dateLabel += `${filters.start_date} – ${filters.end_date}`;
      } else if (filters.start_date) {
        dateLabel += `from ${filters.start_date}`;
      } else {
        dateLabel += `until ${filters.end_date}`;
      }
      pushPill('dates', dateLabel, () => updateAdvancedFilters({ start_date: null, end_date: null }));
    }

    return pills;
  };

  /**
   * Get processed alerts: filter → sort → group
   */
  const getProcessedAlerts = () => {
    let filtered = alerts;

    // Filter by view mode (active, acknowledged, snoozed, archived)
    if (viewMode === 'active') {
      filtered = filtered.filter((a) => a.status === 'active');
    } else if (viewMode === 'acknowledged') {
      filtered = filtered.filter((a) => a.status === 'acknowledged');
    } else if (viewMode === 'snoozed') {
      filtered = filtered.filter((a) => a.status === 'snoozed');
    } else if (viewMode === 'archived') {
      filtered = filtered.filter((a) => a.status === 'archived');
    }

    // Note: Priority/Severity filtering is now done server-side via the Priority dropdown
    // (filterPriority updates filters.severity which is sent to the backend)

    // Note: Category filtering is now done server-side
    // (filterCategories updates filters.categories, sent to backend and converted to alert_types)

    // Note: Search filtering is now done server-side (filters.search sent to backend)
    // No client-side filtering needed since server returns pre-filtered results

    // Apply sorting based on sorting state
    filtered.sort((a, b) => {
      let aValue, bValue;

      // Get sort values based on sort_by column
      switch (sorting.sort_by) {
        case 'severity':
          const severityOrder = { critical: 0, warning: 1, info: 2 };
          aValue = severityOrder[a.severity] ?? 999;
          bValue = severityOrder[b.severity] ?? 999;
          break;
        case 'type':
          aValue = (a.type || '').toLowerCase();
          bValue = (b.type || '').toLowerCase();
          break;
        case 'subject_primary':
          aValue = getSubjectPrimarySortKey(a);
          bValue = getSubjectPrimarySortKey(b);
          break;
        case 'location_name':
          aValue = (a.location_name || '').toLowerCase();
          bValue = (b.location_name || '').toLowerCase();
          break;
        case 'device_name':
          aValue = (a.device_name || '').toLowerCase();
          bValue = (b.device_name || '').toLowerCase();
          break;
        case 'device_identifier':
          aValue = (a.device_identifier || '').toLowerCase();
          bValue = (b.device_identifier || '').toLowerCase();
          break;
        case 'complaint_identifier':
          aValue = (a.complaint_identifier || '').toLowerCase();
          bValue = (b.complaint_identifier || '').toLowerCase();
          break;
        case 'department_name':
          aValue = (a.department_name || '').toLowerCase();
          bValue = (b.department_name || '').toLowerCase();
          break;
        case 'physical_location':
          aValue = (a.physical_location || '').toLowerCase();
          bValue = (b.physical_location || '').toLowerCase();
          break;
        case 'last_detected_at':
        case 'created_at':
        default:
          aValue = new Date(a.last_detected_at || a.created_at).getTime();
          bValue = new Date(b.last_detected_at || b.created_at).getTime();
          break;
      }

      // Compare values
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        // String comparison
        const result = aValue.localeCompare(bValue);
        return sorting.sort_direction === 'asc' ? result : -result;
      } else {
        // Numeric comparison
        if (aValue !== bValue) {
          return sorting.sort_direction === 'asc' ? aValue - bValue : bValue - aValue;
        }
      }

      // Secondary sort by last_detected_at descending if primary sort values are equal
      if (sorting.sort_by !== 'last_detected_at' && sorting.sort_by !== 'created_at') {
        const secondaryA = new Date(a.last_detected_at || a.created_at).getTime();
        const secondaryB = new Date(b.last_detected_at || b.created_at).getTime();
        return secondaryB - secondaryA;
      }

      return 0;
    });

    // Group by selected dimension
    return groupAlerts(filtered, groupBy);
  };

  const handleViewDevice = (alert) => {
    const type = alert?.type || '';
    if (type.startsWith('department_transfer_') && onNavigateToDepartmentTransfers) {
      onNavigateToDepartmentTransfers();
      return;
    }
    if (type.startsWith('device_transfer_') && onNavigateToLocationTransfers) {
      onNavigateToLocationTransfers();
      return;
    }
    const deviceId = alert.device_id || (alert.subject_type === 'device' ? alert.subject_id : null);
    if (deviceId) {
      setViewingDeviceId(deviceId);
      setShowDeviceModal(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleViewComplaint = (alert) => {
    if (alert?.subject_type === 'complaint' && alert.subject_id && onNavigateToComplaintDetail) {
      onNavigateToComplaintDetail(alert.subject_id);
    }
  };

  const handleCloseDeviceView = () => {
    setShowDeviceModal(false);
    setViewingDeviceId(null);
  };

  const handleEditDevice = (device) => {
    if (!canEditDevices) return;
    handleCloseDeviceView();
    if (onDeviceEdit) {
      onDeviceEdit(device);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="alerts-container">
        <div className="loading-state">Loading alerts...</div>
      </div>
    );
  }

  // Device view modal
  if (showDeviceModal && viewingDeviceId) {
    return (
      <div className="alerts-container">
        <DeviceView
          deviceId={viewingDeviceId}
          token={token}
          onEdit={canEditDevices ? handleEditDevice : null}
          onClose={handleCloseDeviceView}
          user={user}
          onNavigateToDashboard={handleCloseDeviceView}
          onNavigateToApprovals={onNavigateToApprovals}
        />
      </div>
    );
  }

  const processedAlerts = getProcessedAlerts();

  return (
    <div className="alerts-container">
      {/* Header Section */}
      <div className="alerts-description-section">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <h1>🔔 System Alerts & Notifications</h1>
          <button
            onClick={toggleHelpModal}
            className="btn-refresh"
            title="View alert help information"
            style={{
              padding: '0.5rem',
              fontSize: '1.2rem',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              lineHeight: 1
            }}
          >
            ℹ️
          </button>
          <button
            onClick={handleRefreshAlerts}
            className="btn-refresh"
            title="Regenerate and refresh alerts from database"
            style={{
              padding: '0.5rem',
              fontSize: '1.2rem',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              lineHeight: 1
            }}
          >
            🔄
          </button>
        </div>
        <p>
          Monitor system alerts and notifications for all devices. Stay informed about critical maintenance
          issues, warranty expirations, and compliance deadlines.
        </p>
      </div>

      <div className="list-default-window-note" role="note">
        <span className="list-default-window-note__icon" aria-hidden="true">ℹ️</span>
        <span>
          Showing alerts from the <strong>last 30 days</strong> by default.
          Use Advanced Filters to view a different date range.
        </span>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Help Modal - ReactDOM Portal */}
      <AlertHelpModal
        isOpen={showHelpModal}
        onClose={() => setShowHelpModal(false)}
      />

      {/* Summary Cards */}
      <div className="alerts-summary-grid">
        <div className="alert-summary-card critical">
          <div className="summary-card-header">
            <span className="summary-card-icon">🔴</span>
            <h3>Critical</h3>
          </div>
          <div className="summary-card-value">{counts.critical}</div>
          <div className="summary-card-label">Requires immediate action</div>
        </div>

        <div className="alert-summary-card warning">
          <div className="summary-card-header">
            <span className="summary-card-icon">⚠️</span>
            <h3>Warnings</h3>
          </div>
          <div className="summary-card-value">{counts.warning}</div>
          <div className="summary-card-label">Review recommended</div>
        </div>

        <div className="alert-summary-card info">
          <div className="summary-card-header">
            <span className="summary-card-icon">ℹ️</span>
            <h3>Info</h3>
          </div>
          <div className="summary-card-value">{counts.info}</div>
          <div className="summary-card-label">For your information</div>
        </div>

        <div className="alert-summary-card total">
          <div className="summary-card-header">
            <span className="summary-card-icon">📌</span>
            <h3>Total Alerts</h3>
          </div>
          <div className="summary-card-value">{counts.total}</div>
          <div className="summary-card-label">All notifications</div>
        </div>
      </div>

      {/* Filters - Phase 1 (Legacy) + Phase 2 (New) */}
      <AlertFilters
        groupBy={groupBy}
        setGroupBy={setGroupBy}
        filterPriority={filterPriority}
        setFilterPriority={setFilterPriority}
        filterCategories={filterCategories}
        setFilterCategories={setFilterCategories}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        categories={filterOptions.categories}
        onPriorityChange={handleSeverityChange}
        onSearchChange={handleSearchChange}
        onCategoryChange={handleCategoryChange}
      />

      {hasAdvancedFilters() && (
        <div className="active-filters">
          <div className="active-filters-header">
            <span className="active-filters-label">Filters Active:</span>
            <button
              className="clear-all-btn"
              onClick={handleClearAllFilters}
              title="Clear all advanced filters"
            >
              Clear All
            </button>
          </div>
          <div className="active-filters-pills">
            {renderAdvancedFilterPills()}
          </div>
        </div>
      )}

      <MoreFiltersButton
        isOpen={showAdvancedPanel}
        hasAdditional={appliedFilterCount > 0}
        onClick={() => {
          if (!showAdvancedPanel) {
            setDrawerFilters({
              location_ids: filters.location_ids,
              subject_types: filters.subject_types,
              department_ids: filters.department_ids,
              device_search: filters.search || '',
              start_date: filters.start_date,
              end_date: filters.end_date,
            });
          }
          setShowAdvancedPanel(!showAdvancedPanel);
        }}
      />

      {/* Advanced Filters Drawer - ReactDOM Portal */}
      {showAdvancedPanel &&
        ReactDOM.createPortal(
          <div className="sidebar-drawer-overlay" onClick={() => setShowAdvancedPanel(false)}>
            <div className="sidebar-drawer" onClick={(e) => e.stopPropagation()}>
              <div className="drawer-title">
                <span>🔧 Advanced Filters</span>
                <button
                  type="button"
                  className="drawer-close-btn"
                  onClick={() => setShowAdvancedPanel(false)}
                  title="Close filters"
                >
                  ✕
                </button>
              </div>

              <div className="drawer-filter-content">
                {/* Location Filter (superadmin — all org locations incl. disabled) */}
                {user?.role === 'superadmin' && (
                <div className="filter-group">
                  <div
                    className="filter-group-title"
                    onClick={() => setExpandedSections(prev => ({ ...prev, location: !prev.location }))}
                  >
                    <span className={`group-arrow ${expandedSections.location ? 'open' : ''}`}>▼</span>
                    <span>📍 Locations</span>
                  </div>
                  {expandedSections.location && (
                    <div className="filter-group-content">
                      <div className="filter-item-wrapper">
                        <LocationFilterMultiSelect
                          locations={locationFilterList}
                          value={drawerFilters.location_ids}
                          onChange={(selected) => {
                            setDrawerFilters((prev) => ({
                              ...prev,
                              location_ids: selected.map((id) => parseInt(id, 10)),
                            }));
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
                )}

                <div className="filter-group">
                  <div
                    className="filter-group-title"
                    onClick={() => setExpandedSections((prev) => ({ ...prev, subjectType: !prev.subjectType }))}
                  >
                    <span className={`group-arrow ${expandedSections.subjectType ? 'open' : ''}`}>▼</span>
                    <span>Subject type</span>
                  </div>
                  {expandedSections.subjectType && (
                    <div className="filter-group-content">
                      <div className="filter-item-wrapper">
                        <select
                          multiple
                          className="filter-select"
                          value={drawerFilters.subject_types}
                          onChange={(e) => {
                            const selected = Array.from(
                              e.target.selectedOptions,
                              (option) => option.value,
                            );
                            setDrawerFilters((prev) => ({ ...prev, subject_types: selected }));
                          }}
                          style={{ minHeight: '100px' }}
                          title="Hold Cmd/Ctrl to select multiple subject types"
                        >
                          {SUBJECT_TYPE_OPTIONS.map((st) => (
                            <option key={st.value} value={st.value}>
                              {st.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}
                </div>

                <div className="filter-group">
                  <div
                    className="filter-group-title"
                    onClick={() => setExpandedSections((prev) => ({ ...prev, department: !prev.department }))}
                  >
                    <span className={`group-arrow ${expandedSections.department ? 'open' : ''}`}>▼</span>
                    <span>Department</span>
                  </div>
                  {expandedSections.department && (
                    <div className="filter-group-content">
                      <div className="filter-item-wrapper">
                        <div className="filter-label-text">Departments</div>
                        <select
                          multiple
                          className="filter-select"
                          value={drawerFilters.department_ids.map(String)}
                          onChange={(e) => {
                            const selectedIds = Array.from(
                              e.target.selectedOptions,
                              (option) => parseInt(option.value, 10),
                            );
                            setDrawerFilters((prev) => ({ ...prev, department_ids: selectedIds }));
                          }}
                          style={{ minHeight: '100px' }}
                          disabled={isDepartmentFilterDisabled}
                          title={
                            isDepartmentFilterDisabled
                              ? 'Only your assigned department is available'
                              : 'Hold Cmd/Ctrl to select multiple departments'
                          }
                        >
                          {departmentFilterOptions.length > 0 ? (
                            departmentFilterOptions.map((dept) => (
                              <option key={dept.id} value={dept.id}>
                                {dept.icon ? `${dept.icon} ` : ''}{dept.name}
                              </option>
                            ))
                          ) : (
                            <option value="" disabled>
                              {isDepartmentScopedRole ? 'No departments assigned' : 'No departments available'}
                            </option>
                          )}
                        </select>
                      </div>
                    </div>
                  )}
                </div>

                <div className="filter-group">
                  <div
                    className="filter-group-title"
                    onClick={() => setExpandedSections((prev) => ({ ...prev, device: !prev.device }))}
                  >
                    <span className={`group-arrow ${expandedSections.device ? 'open' : ''}`}>▼</span>
                    <span>Device ID</span>
                  </div>
                  {expandedSections.device && (
                    <div className="filter-group-content">
                      <input
                        type="text"
                        className="filter-select"
                        placeholder="e.g. EQ-1001"
                        value={drawerFilters.device_search}
                        onChange={(e) =>
                          setDrawerFilters((prev) => ({ ...prev, device_search: e.target.value }))
                        }
                      />
                    </div>
                  )}
                </div>

                {/* Date Range Filter */}
                <div className="filter-group">
                  <div
                    className="filter-group-title"
                    onClick={() => setExpandedSections(prev => ({ ...prev, dateRange: !prev.dateRange }))}
                  >
                    <span className={`group-arrow ${expandedSections.dateRange ? 'open' : ''}`}>▼</span>
                    <span>📅 Date Range</span>
                  </div>
                  {expandedSections.dateRange && (
                    <div className="filter-group-content">
                      <div className="filter-item-wrapper">
                        <div className="filter-label-text">From</div>
                        <input
                          type="date"
                          className="filter-select"
                          value={drawerFilters.start_date || ''}
                          onChange={(e) => setDrawerFilters(prev => ({ ...prev, start_date: e.target.value }))}
                          title="Start date (default: 30 days ago)"
                        />
                      </div>
                      <div className="filter-item-wrapper">
                        <div className="filter-label-text">To</div>
                        <input
                          type="date"
                          className="filter-select"
                          value={drawerFilters.end_date || ''}
                          onChange={(e) => setDrawerFilters(prev => ({ ...prev, end_date: e.target.value }))}
                          title="End date (default: today)"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="drawer-buttons">
                <button
                  className="cancel-btn"
                  onClick={() => setShowAdvancedPanel(false)}
                  title="Close filters without applying changes"
                >
                  Close
                </button>
                <button
                  className="reset-btn"
                  onClick={() => {
                    setDrawerFilters({
                      location_ids: [],
                      subject_types: [],
                      department_ids: [],
                      device_search: '',
                      start_date: null,
                      end_date: null,
                    });
                  }}
                >
                  Reset
                </button>
                <button
                  className="apply-btn"
                  onClick={() => {
                    const deviceSearch = (drawerFilters.device_search || '').trim();
                    setFilters((prev) => ({
                      ...prev,
                      location_ids: drawerFilters.location_ids,
                      subject_types: drawerFilters.subject_types,
                      department_ids: drawerFilters.department_ids,
                      search: deviceSearch || prev.search,
                      start_date: drawerFilters.start_date,
                      end_date: drawerFilters.end_date,
                    }));
                    setPagination((prev) => ({ ...prev, page: 1 }));
                    if (deviceSearch) {
                      setSearchQuery(deviceSearch);
                    }
                    setShowAdvancedPanel(false);
                  }}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>,
          document.body
        )
      }

      {isDisabledLocationFilterBlocked && (
        <DisabledLocationAccessBlockedBanner disabledLocations={blockedDisabledLocations} />
      )}

      <p className="alerts-inbox-hint" style={{ margin: '0 0 12px', fontSize: '0.875rem', color: '#64748b' }}>
        Active alerts need attention. Acknowledge when reviewed, snooze to hide for 7 days, or archive after handling.
        Snoozed alerts return to Active automatically when the snooze period ends.
      </p>

      {/* View Mode Controls - Above Table */}
      <div className="table-controls-section">
        <div className="view-mode-buttons">
          <button
            className={`view-mode-btn ${viewMode === 'active' ? 'active' : ''}`}
            onClick={() => {
              setViewMode('active');
              saveUiPreferencePatch({ alerts_view_mode: 'active' });
            }}
            title="Show new, unacknowledged alerts"
          >
            <span className="view-mode-icon">⭐</span>
            <span className="view-mode-text">Active</span>
          </button>
          <button
            className={`view-mode-btn ${viewMode === 'acknowledged' ? 'active' : ''}`}
            onClick={() => {
              setViewMode('acknowledged');
              saveUiPreferencePatch({ alerts_view_mode: 'acknowledged' });
            }}
            title="Show acknowledged alerts"
          >
            <span className="view-mode-icon">✓</span>
            <span className="view-mode-text">Acknowledged</span>
          </button>
          <button
            className={`view-mode-btn ${viewMode === 'snoozed' ? 'active' : ''}`}
            onClick={() => {
              setViewMode('snoozed');
              saveUiPreferencePatch({ alerts_view_mode: 'snoozed' });
            }}
            title="Show snoozed alerts"
          >
            <span className="view-mode-icon">⏰</span>
            <span className="view-mode-text">Snoozed</span>
          </button>
          <button
            className={`view-mode-btn ${viewMode === 'archived' ? 'active' : ''}`}
            onClick={() => {
              setViewMode('archived');
              saveUiPreferencePatch({ alerts_view_mode: 'archived' });
            }}
            title="Show archived alerts"
          >
            <span className="view-mode-icon">🗂️</span>
            <span className="view-mode-text">Archived</span>
          </button>
        </div>
      </div>

      {/* Alerts Table or Empty State */}
      {Object.keys(processedAlerts).length === 0 ? (
        <div className="no-alerts-message">
          <div className="no-alerts-icon">✅</div>
          <h3>No alerts to display</h3>
          <p>Everything looks good!</p>
        </div>
      ) : (
        <div className="list-with-pagination alerts-table-panel">
          <AlertTable
            groupedAlerts={processedAlerts}
            groupBy={groupBy}
            onViewDevice={handleViewDevice}
            onViewComplaint={handleViewComplaint}
            onUpdateStatus={updateAlertStatus}
            sorting={sorting}
            onSortChange={handleSortChange}
            isRefreshing={isRefreshingAlerts}
          />

          {pagination.total_count > 0 && (
            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.total_pages || 1}
              onPageChange={(page) => setPagination({ ...pagination, page })}
              perPage={pagination.per_page}
              onPerPageChange={(perPage) => {
                saveUiPreferencePatch({ list_page_size: perPage });
                setPagination({ ...pagination, per_page: perPage, page: 1 });
              }}
              totalItems={pagination.total_count}
              itemLabel="alerts"
            />
          )}
        </div>
      )}
    </div>
  );
}

export default Alerts;
